import { useState, useCallback, useRef, useEffect } from "react";
import { authenticatedGet, authenticatedPost } from "../../api/authenticatedFetch";

const ENDPOINT = "/api/v1/forecast-predictability/model-flag-ab";
const POLL_INTERVAL_MS = 3000;

// useModelFlagAB manages the async model-flag A/B evaluation.
//
// Lifecycle:
//   - load()  → GET the last persisted snapshot (called on mount to restore state)
//   - onRun() → POST (starts the background job), then polls GET every 3 s
//               until status leaves "running"
//
// Returns: { status, flags, computedAt, error, onRun, load }
export function useModelFlagAB() {
  const [status, setStatus] = useState("idle");
  const [flags, setFlags] = useState([]);
  const [computedAt, setComputedAt] = useState(null);
  const [error, setError] = useState(null);

  const pollRef = useRef(null);

  const clearPoll = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const applyResponse = useCallback((res) => {
    setStatus(res.status || "idle");
    setFlags(Array.isArray(res.flags) ? res.flags : []);
    setComputedAt(res.computed_at || null);
    setError(res.error || null);
    return res.status;
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await authenticatedGet(ENDPOINT);
      applyResponse(res);
    } catch (err) {
      setError(err);
    }
  }, [applyResponse]);

  const startPolling = useCallback(() => {
    clearPoll();
    pollRef.current = setInterval(async () => {
      try {
        const res = await authenticatedGet(ENDPOINT);
        const newStatus = applyResponse(res);
        if (newStatus !== "running") {
          clearPoll();
        }
      } catch (err) {
        setError(err);
        clearPoll();
      }
    }, POLL_INTERVAL_MS);
  }, [applyResponse, clearPoll]);

  const onRun = useCallback(async () => {
    setStatus("running");
    setError(null);
    try {
      await authenticatedPost(ENDPOINT, {});
    } catch (err) {
      setError(err);
      setStatus("failed");
      return;
    }
    startPolling();
  }, [startPolling]);

  // Tear down the poll interval on unmount so it can't fire on an unmounted component.
  useEffect(() => clearPoll, [clearPoll]);

  return { status, flags, computedAt, error, onRun, load };
}
